import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Clock, Copy, Loader2, Lock, Maximize2, MessageCircle, NotebookText, UserPlus } from 'lucide-react';
import { AvatarView } from '../../components/ui/AvatarView';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { FullscreenOverlay } from '../../components/ui/FullscreenOverlay';
import { LinkifiedText } from '../../components/ui/LinkifiedText';
import { MarkdownReportText } from '../../components/reports/MarkdownReportText';
import { PoweredByZmistBadge } from '../../components/ui/PoweredByZmistBadge';
import { PublicReportHero } from '../../components/channels/PublicReportHero';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { SignalActivityChart } from '../../components/charts/SignalActivityChart';
import { ConfidenceBadge } from '../../components/reports/ConfidenceBadge';
import { EvidenceSection } from '../../components/reports/EvidenceSection';
import { parseEvidenceReport, resolveConfidenceLevel } from '../../lib/evidenceReport';
import { isMultiCharSignalIcon } from '../../lib/signalIcon';
import { DEFAULT_SIGNAL_EMOJI } from '../../components/signal/SignalEditorFields';
import {
  requestJoinChannel,
  fetchChannel,
  fetchSharedChannel,
  fetchSignals,
  fetchTemplates,
  type FetchedSignal,
  type SharedChannelView,
} from '../../api/dataApi';
import { rememberPostLoginRedirect, useStore } from '../../store/StoreContext';
import { REPORT_TYPE, type ChartVariant, type Message, type Template } from '../../types';

const VALID_CHART_VARIANTS: ChartVariant[] = ['number', 'bar', 'line', 'circle'];
function asChartVariant(value: string): ChartVariant {
  return (VALID_CHART_VARIANTS as string[]).includes(value) ? (value as ChartVariant) : 'bar';
}

const PUBLIC_TABS = ['messages', 'charts', 'reports'] as const;
type PublicTab = (typeof PUBLIC_TABS)[number];
function validPublicTab(v: string | undefined): PublicTab | null {
  return (PUBLIC_TABS as readonly string[]).includes(v ?? '') ? (v as PublicTab) : null;
}

// The shareable URL for one tab, optionally its full-screen view - see
// App.tsx's `/shared/:nickname/:token/:tab?/:mode?` route. Visiting this
// directly (no tab/mode) still works exactly as before - PublicChannelViewScreen
// falls back to the owner's own viewType/chart-fullscreen defaults.
function buildPublicUrl(nickname: string, token: string, tab: PublicTab, full: boolean): string {
  return `/shared/${nickname}/${token}/${tab}${full ? '/full' : ''}`;
}

// Reports have no per-message emoji of their own - one standard icon for
// every report, same as MessagesScreen.tsx's identical REPORT_EMOJI.
const REPORT_EMOJI = '📝';

// Older signal messages carry " · <title>" baked into their stored text -
// see MessagesScreen.tsx's displayText for the full history. Scoped to
// signal messages only (message.type set) via the *last* " · ".
function publicDisplayText(message: FetchedSignal): string {
  if (!message.type) return message.text;
  const separatorIndex = message.text.lastIndexOf(' · ');
  return separatorIndex === -1 ? message.text : message.text.slice(0, separatorIndex);
}

// dispatchSignal always formats a signal message as "<emoji> <label>" -
// same split MessagesScreen.tsx's SignalMedallion uses.
function splitSignalEmoji(text: string): { emoji: string; label: string } {
  const spaceIndex = text.indexOf(' ');
  if (spaceIndex === -1) return { emoji: '', label: text };
  return { emoji: text.slice(0, spaceIndex), label: text.slice(spaceIndex + 1) };
}

// The text shown next to the medallion - the signal's label with its own
// embedded emoji stripped back off (SignalMedallion renders that emoji in
// the circle instead), or the message's plain text when it isn't a signal
// at all.
function medallionLabel(message: FetchedSignal): string {
  if (!message.type) return message.text;
  const text = publicDisplayText(message);
  return message.type === REPORT_TYPE ? text : splitSignalEmoji(text).label;
}

// Simplified, read-only counterpart to MessagesScreen.tsx's SignalMedallion -
// same round colored-circle look for a signal/report message (the "medallion"
// this screen was missing - every message rendered as a plain text row with
// no icon at all, unlike the real chat view), just without the tap-to-edit/
// reply/save-as-signal affordances this read-only public view has no use for.
// Renders nothing for a plain typed message (message.type unset), matching
// MessagesScreen's own bubble-only treatment of those.
function SignalMedallion({ message, templates }: { message: FetchedSignal; templates: Template[] }) {
  if (!message.type) return null;
  const isReport = message.type === REPORT_TYPE;
  const { emoji: textEmoji } = isReport ? { emoji: REPORT_EMOJI } : splitSignalEmoji(publicDisplayText(message));
  let templateEmoji: string | null = null;
  let colorHex: string | null = null;
  if (!isReport) {
    for (const t of templates) {
      const match = t.signals.find((s) => s.name === message.type);
      if (match) {
        templateEmoji = match.emoji;
        colorHex = match.colorHex;
        break;
      }
    }
  }
  const emoji = isReport ? REPORT_EMOJI : templateEmoji || textEmoji || DEFAULT_SIGNAL_EMOJI;
  const accent = colorHex ?? '#6B7280';
  return (
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 shrink-0 ${isMultiCharSignalIcon(emoji) ? 'text-xs font-semibold' : 'text-xl'}`}
      style={{ background: `${accent}1f`, borderColor: accent }}
    >
      {emoji}
    </div>
  );
}

// Public, unauthenticated route (see App.tsx - deliberately outside both
// RequireGuest and RequireAuth, and outside AppLayout's sidebar chrome,
// since anyone with the link - no account - needs to reach this).
// Read-only: no compose box, no signal buttons, no edit/delete, no chart-
// type/interval changes persisted back to the channel (all three tabs below
// are local-only UI state on top of real fetched data). The one write this
// screen allows is a join *request* (POST /shared/{nickname}/{token}/
// request-join) when the owner has turned "request on join" on (view.
// requestToJoin - see backend/workflows/channel/request_access.wsl) -
// gated on being logged in (see the isLoggedIn branch below, and
// goToAuth's round-trip through /login or /register back to this exact
// URL). This used to call addChannelMember (POST /channels/{nickname}/
// members) directly as an instant self-join, but that endpoint is owner/
// manageChannel only (see member_add.wsl) - a visitor calling it always
// 403'd. request_access.wsl is what member_add.wsl's own header comment
// always intended for self-service joins.
export function PublicChannelViewScreen() {
  const { nickname, token, tab: tabParam, mode: modeParam } = useParams();
  const { isLoggedIn, profile, channels, cloneChannel } = useStore();
  const navigate = useNavigate();

  const [view, setView] = useState<SharedChannelView | null>(null);
  const [messages, setMessages] = useState<FetchedSignal[]>([]);
  // True while the paginated fetchSignals loop (see the effect below) is
  // still walking the channel's history - distinct from `loading` (the
  // initial POST /shared/.../ verify call) so the tabs/content area can
  // show its own spinner instead of silently rendering "No messages yet"
  // for however long a channel with real history takes to fully page in.
  const [contentLoading, setContentLoading] = useState(true);
  const [templates, setTemplates] = useState<Template[]>([]);
  // Seeded from the URL's `:tab` segment when it's a real one (a shared
  // deep link) - `load()` below only falls back to the owner's own
  // viewType preset when the URL didn't name a tab at all, so a link into
  // a specific tab always wins over that preset.
  const [tab, setTabState] = useState<PublicTab>(validPublicTab(tabParam) ?? 'messages');
  // Messages/Reports' own full-screen view (the Charts tab's is owned by
  // SignalActivityChart itself - see its startFullscreen/onFullscreenChange
  // props). Seeded from the URL's `:mode` segment so a `/messages/full` or
  // `/reports/full` link lands already maximized.
  const [fullscreenOpen, setFullscreenOpen] = useState(modeParam === 'full' && validPublicTab(tabParam) !== null && tabParam !== 'charts');
  const [chartType, setChartType] = useState<ChartVariant>('bar');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [requestStatus, setRequestStatus] = useState<'accepted' | 'pending' | null>(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [joinError, setJoinError] = useState('');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');

  const load = async (pwd: string) => {
    if (!nickname || !token) return;
    setLoading(true);
    setError('');
    try {
      const result = await fetchSharedChannel(nickname, token, pwd);
      setView(result);
      setNeedsPassword(false);
      // A URL that already names a real tab (a shared deep link) wins -
      // only fall back to the owner's viewType preset when it doesn't.
      // "report" design shows the chart permanently in the hero block
      // (see PublicReportHero) rather than behind a tab, so a 'charts'
      // viewType preset falls back to 'messages' there instead of landing
      // on a tab that design doesn't offer.
      const isReportDesign = result.viewDesign === 'report';
      const wantsTab = result.viewType === 'charts' || result.viewType === 'reports' ? result.viewType : 'messages';
      setTabState(validPublicTab(tabParam) ?? (isReportDesign && wantsTab === 'charts' ? 'messages' : wantsTab));
      setChartType(asChartVariant(result.viewChartType));
      // Messages/templates are fetched by the effect below instead of
      // here, so they load only once the viewer is actually entitled to
      // see them - see `contentUnlocked`. A request-to-join channel keeps
      // its content hidden from a non-member visitor until the owner
      // approves them.
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load this shared channel.';
      if (message === 'incorrect password') {
        setNeedsPassword(true);
        if (pwd) setError('Incorrect password.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nickname, token]);

  // SignalActivityChart (shared with ChartScreen - see its own comment)
  // takes the app's internal Message shape. FetchedSignal already carries
  // everything that needs except isMine, which the chart never reads -
  // matches MessagesScreen.loadMessages' identical type-normalizing.
  const chartMessages: Message[] = useMemo(
    () =>
      messages.map((m) => ({
        id: m.id,
        sender: m.sender,
        text: m.text,
        date: m.date,
        isMine: false,
        type: !m.type || m.type === 'message' ? undefined : m.type,
        value: m.value,
      })),
    [messages]
  );

  // A report is just a fetched signal tagged type: REPORT_TYPE (see
  // types.ts) - the Reports tab below is a filtered view of `messages`,
  // same as ReportsScreen.tsx, rather than its own fetch.
  const reports = useMemo(() => messages.filter((m) => m.type === REPORT_TYPE), [messages]);

  // Sends a not-logged-in visitor to auth and back: rememberPostLoginRedirect
  // stashes this exact shared URL so App.tsx's RequireGuest returns them
  // here (instead of the default /channels) once they're logged in.
  const goToAuth = (path: '/login' | '/register') => {
    rememberPostLoginRedirect(`/shared/${nickname}/${token}`);
    navigate(path);
  };

  // Submits a join request (see request_access.wsl) - the server derives
  // the requester's userId from their own session token, not anything
  // decoded client-side; `profile` (nickname/name/avatarSymbol) is this
  // account's own already-known data from StoreContext. `password` is
  // whatever unlocked the read-only view above (empty if this link isn't
  // password-protected) - request_access.wsl re-verifies it itself.
  // A visitor who's already an accepted member (their earlier request was
  // approved, or they were added directly) has this channel in their own
  // "Channels" list - the share link has no server-side "what's my status"
  // endpoint (shared_view.wsl is fully unauthenticated), so without this
  // check a reload after approval drops back to showing "Request to join"
  // as if nothing happened. A pending request still isn't restorable
  // across reloads this way (it's not in user:channels until approval) -
  // that stays session-only via requestStatus below.
  const alreadyMember = isLoggedIn && !!nickname && channels.some((c) => c.nickname === nickname);
  const effectiveRequestStatus: 'accepted' | 'pending' | null = alreadyMember ? 'accepted' : requestStatus;

  // "Clone this channel" (roadmap #8's "Clone CTA embedded on public
  // reports") - a completely separate action from requesting to join: it
  // copies the channel's signal-template structure into a brand new
  // channel the visitor owns outright, no approval needed. Not shown to
  // someone who already has this channel (owner or member - alreadyMember
  // covers both, same as the "Go to your channels" branch above).
  // SharedChannelView deliberately omits templateIds and other owner-only
  // fields (see shared_view.wsl's header comment), so this fetches the
  // full channel doc first - GET /channel/{nickname} has no auth check at
  // all (see channel/read.wsl), same public-read fact StatusReportScreen.tsx
  // already relies on.
  const clone = async () => {
    if (!isLoggedIn) {
      goToAuth('/register');
      return;
    }
    if (!nickname) return;
    setCloning(true);
    setCloneError('');
    try {
      const full = await fetchChannel(nickname);
      if (!full) throw new Error('Unable to load this channel to clone.');
      const created = await cloneChannel(full, 'public_channel_view');
      navigate(`/channels/${created.id}/edit`);
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Unable to clone this channel.');
    } finally {
      setCloning(false);
    }
  };

  // A read-only share link (requestToJoin off) shows its content to
  // anyone with the link, as before. When the owner requires approval to
  // join (requestToJoin on), the messages/charts/reports stay hidden from
  // a visitor until they've been approved - a pending request is not
  // enough. (This is a client-side gate consistent with the rest of the
  // app's access model, not a hard security boundary - request-to-join
  // state isn't re-checked server-side when fetching signals below, only
  // the share link's own enabled+token match is - see signal/list.wsl's
  // AnonymousCheckShareToken state.)
  const contentUnlocked = !view?.requestToJoin || effectiveRequestStatus === 'accepted';

  // Owner-selectable look (Channel.viewDesign, set on ChannelManageScreen) -
  // "report" shows PublicReportHero's stat tiles + chart permanently above
  // the tabs instead of behind a Charts tab. See that component's header
  // comment for what it does and doesn't compute.
  const isReportDesign = view?.viewDesign === 'report';

  // Switching tabs (the SegmentedControl) always drops any open full-screen
  // view and updates the URL to match - so the address bar is always a
  // faithful "share this to get exactly what I'm looking at right now"
  // link, same intent as openFullscreen/closeFullscreen just below.
  const setTab = (t: PublicTab) => {
    setTabState(t);
    setFullscreenOpen(false);
    if (nickname && token) navigate(buildPublicUrl(nickname, token, t, false), { replace: true });
  };

  // Messages/Reports' own Maximize button + overlay (see their render
  // below) - the Charts tab doesn't use these at all, it has its own
  // built-in full-screen handled by SignalActivityChart's
  // startFullscreen/onFullscreenChange props, wired a few lines down.
  const openFullscreen = () => {
    setFullscreenOpen(true);
    if (nickname && token) navigate(buildPublicUrl(nickname, token, tab, true), { replace: true });
  };
  const closeFullscreen = () => {
    setFullscreenOpen(false);
    if (nickname && token) navigate(buildPublicUrl(nickname, token, tab, false), { replace: true });
  };
  // The chart manages its OWN fullscreen state internally (it's the same
  // component ChartScreen.tsx uses, Maximize button included) - this just
  // mirrors that open/close back into the URL so a visitor can still copy
  // a "straight into the maximized chart" link. Independent of `tab`
  // (the chart is always rendered as "charts", whether that's the
  // selected tab under the classic design or the permanently-shown hero
  // chart under the report design).
  const onChartFullscreenChange = (open: boolean) => {
    if (nickname && token) navigate(buildPublicUrl(nickname, token, 'charts', open), { replace: true });
  };
  const chartStartFullscreen = modeParam === 'full' && tabParam === 'charts';

  useEffect(() => {
    if (!view || !nickname) return;
    // Members-only content never becomes fetchable - stop showing a
    // spinner for data that was never going to load. If the viewer is
    // later approved, contentUnlocked flips and this effect re-runs.
    if (!contentUnlocked) {
      setContentLoading(false);
      return;
    }
    if (messages.length > 0) return;
    let cancelled = false;
    (async () => {
      // token is the share-link token from the URL - required so an
      // anonymous (no-account) visitor passes signal/list.wsl's anonymous
      // share-link fallback instead of 401ing (see fetchSignals's comment
      // and signal/list.wsl's header comment for the history of this bug:
      // GET /signals/{channelId} used to hard-require a session before
      // ever checking the channel's share-link status).
      //
      // A single DEFAULT_PAGE_SIZE (20) page used to be all this screen
      // ever fetched, which silently starved both the Reports tab (a
      // report can easily be older than the last 20 signals) and the
      // Charts tab (the owner's preset interval - e.g. "All" - has
      // nothing to show beyond that one page). This has no "load more"
      // control like the authenticated Messages screen does, so it pages
      // through the channel's whole history up front instead, bounded by
      // PUBLIC_VIEW_MAX_PAGES below so a pathologically large channel
      // can't hang this read-only view.
      // 100 matches backend/config/pagination.json's maxPageSize - asking
      // for more just gets silently clamped there, which previously threw
      // this loop's offset tracking off (it kept advancing by the
      // requested page size, not the clamped one actually returned, so
      // every page after the first skipped a chunk of signals - on a
      // channel with any real history this could skip right over every
      // report, landing on "nothing in the Reports tab" even though
      // reports exist).
      const PAGE_SIZE = 100;
      const PUBLIC_VIEW_MAX_PAGES = 50; // 5,000 signals
      let offset = 0;
      let all: FetchedSignal[] = [];
      for (let page = 0; page < PUBLIC_VIEW_MAX_PAGES; page++) {
        const signalsPage = await fetchSignals(nickname, offset, PAGE_SIZE, token);
        if (cancelled) return;
        all = all.concat(signalsPage.signals);
        if (!signalsPage.pagination.hasMore || signalsPage.signals.length === 0) break;
        offset += signalsPage.pagination.limit || PAGE_SIZE;
      }
      const templatesPage = await fetchTemplates();
      if (cancelled) return;
      setMessages(all.sort((a, b) => (a.date < b.date ? 1 : -1)));
      setTemplates(templatesPage?.templates ?? []);
    })()
      .catch(() => {
        /* a failed signals/templates fetch just leaves the tabs empty - the
           read-only view is still useful without them */
      })
      .finally(() => {
        if (!cancelled) setContentLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, nickname, contentUnlocked, messages.length]);

  const requestJoin = async () => {
    if (!nickname || !token) return;
    setRequesting(true);
    setJoinError('');
    try {
      const result = await requestJoinChannel(
        nickname,
        token,
        { nickname: profile.nickname, name: profile.name, avatarSymbol: profile.avatarSymbol },
        { password, message: requestMessage.trim() }
      );
      setRequestStatus(result.status === 'accepted' ? 'accepted' : 'pending');
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Unable to submit your join request.');
    } finally {
      setRequesting(false);
    }
  };

  // Shared between the normal inline list and the full-screen overlay
  // below (openFullscreen/closeFullscreen) - same list, just rendered
  // bigger/alone, same as SignalActivityChart's own renderChartCard does
  // for the chart.
  function renderMessagesList() {
    return messages.length === 0 ? (
      <EmptyState icon={MessageCircle} title="No messages yet" subtitle="This channel has no activity yet." />
    ) : (
      messages.map((message) => (
        <div key={message.id} className="rounded-2xl bg-white dark:bg-white/5 border border-black/5 dark:border-white/10 p-4 flex items-start gap-3">
          <SignalMedallion message={message} templates={templates} />
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <span className="text-xs font-semibold text-black/50 dark:text-white/50">{message.sender || 'Unknown'}</span>
            <LinkifiedText text={medallionLabel(message)} className="text-[15px] whitespace-pre-wrap" />
            <span className="text-[11px] text-black/40 dark:text-white/40">
              {message.date ? new Date(message.date).toLocaleString() : ''}
            </span>
          </div>
        </div>
      ))
    );
  }

  function renderReportsList() {
    return reports.length === 0 ? (
      <EmptyState icon={NotebookText} title="No reports" subtitle="This channel has no reports yet." />
    ) : (
      reports.map((report) => {
        // Same "only a Multi-Channel Report was ever prompted with this
        // format" reasoning as ReportsScreen.tsx.
        const isMultiChannel = !!report.additionalChannelIds?.length;
        const evidence = isMultiChannel ? parseEvidenceReport(report.text) : null;
        const confidenceLevel = resolveConfidenceLevel(report.confidence, evidence?.confidenceLevel ?? null);
        return (
          <div key={report.id} className="rounded-2xl bg-white dark:bg-white/5 border border-black/5 dark:border-white/10 p-4 flex flex-col gap-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-black/50 dark:text-white/50">{report.sender || 'Unknown'}</span>
              {confidenceLevel && <ConfidenceBadge level={confidenceLevel} />}
            </div>
            {evidence ? (
              <div className="flex flex-col gap-2.5">
                <EvidenceSection title="Direct answer" text={evidence.directAnswer} />
                <EvidenceSection title="Key evidence" text={evidence.keyEvidence} />
                <EvidenceSection title="Relationships across channels" text={evidence.relationships} />
                <EvidenceSection
                  title={confidenceLevel ? 'Confidence reasoning' : 'Confidence'}
                  text={evidence.confidenceReason}
                />
                {!!report.confidenceFactors?.length && (
                  <EvidenceSection
                    title="Confidence factors (computed)"
                    text={report.confidenceFactors.map((f) => `• ${f}`).join('\n')}
                  />
                )}
                <EvidenceSection title="Conflicting evidence" text={evidence.conflictingEvidence} />
                <EvidenceSection title="Suggested next question" text={evidence.nextQuestion} />
              </div>
            ) : (
              <MarkdownReportText text={report.text} className="text-[15px] flex flex-col gap-1" />
            )}
            <span className="text-[11px] text-black/40 dark:text-white/40">
              {report.date ? new Date(report.date).toLocaleString() : ''}
            </span>
          </div>
        );
      })
    );
  }

  // Small header + Maximize button above the Messages/Reports list,
  // matching the look of SignalActivityChart's own chart-card header
  // button - same affordance, same icon, for the two tabs that don't
  // already have one built in.
  function renderMaximizeHeader(label: string) {
    return (
      <div className="flex items-center justify-between px-1 shrink-0">
        <span className="text-sm font-semibold">{label}</span>
        <button
          type="button"
          onClick={openFullscreen}
          aria-label={`Open ${label.toLowerCase()} full screen`}
          title="Full screen"
          className="shrink-0 p-1 rounded-md text-black/40 dark:text-white/40 hover:text-current hover:bg-black/5 dark:hover:bg-white/10"
        >
          <Maximize2 size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#f4f4f6] dark:bg-black flex flex-col items-center px-4 py-6 md:pb-14 overflow-hidden">
      <div className="w-full max-w-lg flex flex-col gap-4 h-full min-h-0">
        {loading && !view && !needsPassword && <span className="text-sm text-black/50 dark:text-white/50 text-center">Loading...</span>}

        {needsPassword && (
          <div className="flex flex-col items-center gap-4 text-center">
            <Lock size={32} className="text-black/40 dark:text-white/40" />
            <div>
              <h1 className="text-lg font-semibold m-0">This link is password-protected</h1>
              <p className="text-sm text-black/50 dark:text-white/50 m-0">Enter the password to view this channel.</p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                load(password);
              }}
              className="w-full flex flex-col gap-2"
            >
              <input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
              />
              <Button type="submit" full disabled={loading}>
                {loading ? 'Checking...' : 'View channel'}
              </Button>
              {error && <span className="text-sm text-red-500">{error}</span>}
            </form>
          </div>
        )}

        {!needsPassword && error && !view && (
          <EmptyState icon={Lock} title="This link isn't available" subtitle={error} />
        )}

        {view && (
          <>
            <div className="flex flex-col items-center text-center gap-2 shrink-0">
              <AvatarView symbol={view.avatarSymbol} colorHex={view.accentHex} imageKey={view.avatarImageKey} size={64} zoomable />
              <h1 className="text-xl font-bold m-0">{view.title}</h1>
              {view.description && <p className="text-sm text-black/50 dark:text-white/50 m-0">{view.description}</p>}
              <span className="text-xs text-black/40 dark:text-white/40">Read-only shared view · {view.nickname}</span>
              {/* #11 pricing enforcement: hidden when any owner is on a
                  badgeRemovable tier (Founding+) - see
                  channel/sharelink.ResolveBadgeRemovable. */}
              {!view.badgeRemovable && <PoweredByZmistBadge />}
              {!alreadyMember && (
                <Button variant="bordered" onClick={clone} loading={cloning} disabled={cloning}>
                  <Copy size={16} />
                  Clone this channel
                </Button>
              )}
              {cloneError && <span className="text-sm text-red-500">{cloneError}</span>}
            </div>

            {/* Only shown when the owner has "request on join" turned on
                (view.requestToJoin) - otherwise this link is read-only
                only, matching backend/workflows/channel/shared_view.wsl's
                gating. */}
            {view.requestToJoin && (
              <div className="flex flex-col items-center gap-2 text-center shrink-0">
                {effectiveRequestStatus === 'accepted' ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 size={18} />
                    <span>You're already a member of this channel.</span>
                    <Button variant="plain" onClick={() => navigate('/channels')}>
                      Go to your channels
                    </Button>
                  </div>
                ) : effectiveRequestStatus === 'pending' ? (
                  <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <Clock size={18} />
                    <span>Your request to join is waiting for approval.</span>
                  </div>
                ) : isLoggedIn ? (
                  <div className="w-full flex flex-col items-center gap-2">
                    <input
                      value={requestMessage}
                      onChange={(e) => setRequestMessage(e.target.value)}
                      placeholder="Add a note for the owner (optional)"
                      maxLength={280}
                      className="w-full rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
                    />
                    <Button onClick={requestJoin} loading={requesting}>
                      <UserPlus size={16} />
                      Request to join
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-sm text-black/50 dark:text-white/50">Log in or sign up to request to join this channel.</span>
                    <div className="flex gap-2">
                      <Button onClick={() => goToAuth('/login')}>Log in</Button>
                      <Button variant="secondary" onClick={() => goToAuth('/register')}>
                        Sign up
                      </Button>
                    </div>
                  </div>
                )}
                {joinError && <span className="text-sm text-red-500">{joinError}</span>}
              </div>
            )}

            {!contentUnlocked ? (
              <div className="flex-1 min-h-0 flex items-center justify-center">
                <EmptyState
                  icon={Lock}
                  title="Members only"
                  subtitle={
                    effectiveRequestStatus === 'pending'
                      ? "Your request is waiting for approval. You'll see this channel's messages, charts, and reports once the owner approves it."
                      : "This channel's owner approves everyone before they join. Request to join to see its messages, charts, and reports."
                  }
                />
              </div>
            ) : (
              <>
            {isReportDesign && (
              <PublicReportHero
                messages={chartMessages}
                templates={templates}
                chartType={chartType}
                onChartTypeChange={setChartType}
                accentHex={view.accentHex}
                locked={!view.publicChartFiltersAllowed}
                initialSettings={view.chartSettings}
                startFullscreen={chartStartFullscreen}
                onFullscreenChange={onChartFullscreenChange}
                storageKey={`chart.public.${nickname}`}
              />
            )}
            <div className="shrink-0">
              <SegmentedControl
                options={
                  isReportDesign
                    ? [
                        { value: 'messages', label: 'Signals' },
                        { value: 'reports', label: 'Reports' },
                      ]
                    : [
                        { value: 'messages', label: 'Signals' },
                        { value: 'charts', label: 'Charts' },
                        { value: 'reports', label: 'Reports' },
                      ]
                }
                value={tab}
                onChange={setTab}
              />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pb-4">
              {contentLoading && messages.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <Loader2 size={28} className="animate-spin text-black/40 dark:text-white/40" />
                  <span className="text-sm text-black/50 dark:text-white/50">Loading {tab}…</span>
                </div>
              ) : (
                <>
              {tab === 'messages' && (
                <>
                  {messages.length > 0 && renderMaximizeHeader('Signals')}
                  {renderMessagesList()}
                </>
              )}

              {!isReportDesign && tab === 'charts' && (
                <SignalActivityChart
                  messages={chartMessages}
                  templates={templates}
                  chartType={chartType}
                  onChartTypeChange={setChartType}
                  locked={!view.publicChartFiltersAllowed}
                  initialSettings={view.chartSettings}
                  startFullscreen={chartStartFullscreen}
                  onFullscreenChange={onChartFullscreenChange}
                  storageKey={`chart.public.${nickname}`}
                />
              )}

              {tab === 'reports' && (
                <>
                  {reports.length > 0 && renderMaximizeHeader('Reports')}
                  {renderReportsList()}
                </>
              )}
                </>
              )}
            </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Messages/Reports' own full-screen view - see openFullscreen/
          closeFullscreen's comment. The Charts tab doesn't need one of
          these: SignalActivityChart renders its own FullscreenOverlay
          internally. */}
      <FullscreenOverlay open={fullscreenOpen && tab === 'messages'} onClose={closeFullscreen} title="Signals">
        <div className="max-w-2xl mx-auto w-full flex flex-col gap-3">{renderMessagesList()}</div>
      </FullscreenOverlay>
      <FullscreenOverlay open={fullscreenOpen && tab === 'reports'} onClose={closeFullscreen} title="Reports">
        <div className="max-w-2xl mx-auto w-full flex flex-col gap-3">{renderReportsList()}</div>
      </FullscreenOverlay>
    </div>
  );
}
